import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Filter, MapPin, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "#/../convex/_generated/api";
import { DiscModelFilter } from "#/components/browse/DiscModelFilter";
import { FlightFilter } from "#/components/browse/FlightFilter";
import { MultiSelectFilter } from "#/components/browse/MultiSelectFilter";
import { Badge } from "#/components/ui/badge";
import { Button } from "#/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "#/components/ui/popover";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "#/components/ui/sheet";
import { Slider } from "#/components/ui/slider";
import {
	COLORS,
	CONDITION_SLIDER,
	type ColorEntry,
	DISC_CLASSES,
	DYED_GRADIENT,
	SHIPS_TO_OPTIONS,
	TRANSPARENT_PATTERN,
} from "#/lib/constants";
import {
	countActiveFilters,
	EMPTY_FILTERS,
	type FilterState,
} from "#/lib/listingFilters";
import { cn } from "#/lib/utils";

type FilterBarProps = {
	filters: FilterState;
	availableCountries: string[];
	availablePlasticNames: string[] | null;
	onChange: (next: Partial<FilterState>) => void;
	userCity: string | null;
	userHasCoords: boolean;
};

function FilterButton({
	label,
	activeCount,
	children,
}: {
	label: string;
	activeCount: number;
	children: React.ReactNode;
}) {
	const [open, setOpen] = useState(false);
	const isActive = activeCount > 0;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className={cn(
						"h-8 shrink-0 rounded-full px-3 text-xs font-medium gap-1.5",
						isActive
							? "border-primary bg-primary/10 text-primary-accessible hover:bg-primary/15 hover:text-primary-accessible"
							: "hover:border-foreground/30",
					)}
				>
					{label}
					{isActive && (
						<span className="flex size-4 items-center justify-center rounded-full bg-primary-accessible text-[10px] font-bold text-primary-foreground leading-none">
							{activeCount}
						</span>
					)}
					<ChevronDown
						className={cn(
							"size-3 transition-transform",
							open && "rotate-180",
							isActive ? "text-primary/70" : "text-muted-foreground",
						)}
					/>
				</Button>
			</PopoverTrigger>
			<PopoverContent
				align="start"
				sideOffset={6}
				className="w-auto min-w-[200px] p-2"
				onInteractOutside={() => setOpen(false)}
			>
				{children}
			</PopoverContent>
		</Popover>
	);
}

function ColorDot({ color }: { color: ColorEntry }) {
	return (
		<span
			aria-hidden
			className="block size-3.5 shrink-0 rounded-full border border-black/10 shadow-sm"
			style={
				color.dyed
					? { background: DYED_GRADIENT }
					: color.transparent
						? { background: TRANSPARENT_PATTERN }
						: { backgroundColor: color.hex }
			}
		/>
	);
}

function RangeSlider({
	min,
	max,
	step = 1,
	filterMin,
	filterMax,
	onChange,
	unit,
}: {
	min: number;
	max: number;
	step?: number;
	filterMin: number | "";
	filterMax: number | "";
	onChange: (min: number | "", max: number | "") => void;
	unit: string;
}) {
	const lo = filterMin !== "" ? filterMin : min;
	const hi = filterMax !== "" ? filterMax : max;
	const [display, setDisplay] = useState([lo, hi]);

	useEffect(() => {
		setDisplay([lo, hi]);
	}, [lo, hi]);

	return (
		<div className="flex flex-col gap-2 p-1 w-[200px]">
			<div className="flex items-center justify-between">
				<span className="text-xs tabular-nums text-muted-foreground">
					{display[0]}
					{unit} – {display[1]}
					{unit}
				</span>
			</div>
			<Slider
				min={min}
				max={max}
				step={step}
				value={display as [number, number]}
				onValueChange={setDisplay}
				onValueCommit={(vals) =>
					onChange(
						vals[0] === min ? "" : vals[0],
						vals[1] === max ? "" : vals[1],
					)
				}
				className="w-full"
			/>
			<div className="flex justify-between text-[10px] text-muted-foreground">
				<span>
					{min}
					{unit}
				</span>
				<span>
					{max}
					{unit}
				</span>
			</div>
		</div>
	);
}

function ConditionSlider({
	condition,
	onChange,
}: {
	condition: string[];
	onChange: (condition: string[]) => void;
}) {
	const maxIdx = CONDITION_SLIDER.length - 1;

	function toSliderRange(conds: string[]): [number, number] {
		if (conds.length === 0) return [0, maxIdx];
		const idxs = conds
			.map((c) => CONDITION_SLIDER.findIndex((s) => s.value === c))
			.filter((i) => i >= 0);
		return [Math.min(...idxs), Math.max(...idxs)];
	}

	function toCondition(lo: number, hi: number): string[] {
		if (lo === 0 && hi === maxIdx) return [];
		return CONDITION_SLIDER.slice(lo, hi + 1).map((c) => c.value);
	}

	const [lo, hi] = toSliderRange(condition);
	const [display, setDisplay] = useState<[number, number]>([lo, hi]);

	useEffect(() => {
		setDisplay([lo, hi]);
	}, [lo, hi]);

	const { t } = useTranslation("browse");
	return (
		<div className="flex flex-col gap-2 p-1 w-[200px]">
			<div className="flex items-center justify-between">
				<span className="text-xs tabular-nums text-muted-foreground">
					{t(`conditions.${CONDITION_SLIDER[display[0]].value}`)} –{" "}
					{t(`conditions.${CONDITION_SLIDER[display[1]].value}`)}
				</span>
			</div>
			<Slider
				min={0}
				max={maxIdx}
				step={1}
				value={display}
				onValueChange={(vals) => setDisplay(vals as [number, number])}
				onValueCommit={(vals) => onChange(toCondition(vals[0], vals[1]))}
				className="w-full"
			/>
			<div className="flex justify-between text-[10px] text-muted-foreground">
				<span>{t("conditionSlider.beat")}</span>
				<span>{t("conditionSlider.likeNew")}</span>
			</div>
		</div>
	);
}

const DISTANCE_MIN = 5;
const DISTANCE_MAX = 500;

function DistanceSlider({
	value,
	onChange,
}: {
	value: number | "";
	onChange: (km: number | "") => void;
}) {
	const current = value !== "" ? value : DISTANCE_MAX;
	const [display, setDisplay] = useState(current);

	useEffect(() => {
		setDisplay(value !== "" ? value : DISTANCE_MAX);
	}, [value]);

	const isActive = value !== "";

	return (
		<div className="flex flex-col gap-2 px-1">
			<div className="flex items-center justify-between">
				<span className="text-xs tabular-nums text-muted-foreground">
					{isActive ? `≤ ${display} km` : "Any distance"}
				</span>
				{isActive && (
					<button
						type="button"
						onClick={() => onChange("")}
						className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
					>
						Clear
					</button>
				)}
			</div>
			<Slider
				min={DISTANCE_MIN}
				max={DISTANCE_MAX}
				step={5}
				value={[display]}
				onValueChange={([v]) => setDisplay(v)}
				onValueCommit={([v]) => onChange(v)}
				className="w-full"
			/>
			<div className="flex justify-between text-[10px] text-muted-foreground">
				<span>{DISTANCE_MIN} km</span>
				<span>{DISTANCE_MAX} km</span>
			</div>
		</div>
	);
}

type FilterControlsProps = {
	filters: FilterState;
	availableCountries: string[];
	onChange: (next: Partial<FilterState>) => void;
	manufacturers: { _id: string; name: string }[];
	visiblePlastics: { _id: string; name: string }[];
	priceActive: number;
	weightActive: number;
	flightActive: number;
	hasAny: boolean;
	userCity: string | null;
	userHasCoords: boolean;
};

function FilterControls({
	filters,
	availableCountries,
	onChange,
	manufacturers,
	visiblePlastics,
	priceActive,
	weightActive,
	flightActive,
	hasAny,
	userCity,
	userHasCoords,
}: FilterControlsProps) {
	const { t } = useTranslation("browse");

	function toggleMulti(
		key: keyof Pick<
			FilterState,
			| "discClass"
			| "condition"
			| "color"
			| "shipsTo"
			| "country"
			| "plasticType"
			| "discModelIds"
			| "manufacturerIds"
		>,
		value: string,
	) {
		const current = filters[key];
		const next = current.includes(value)
			? current.filter((v) => v !== value)
			: [...current, value];
		onChange({ [key]: next });
	}

	return (
		<>
			{manufacturers.length > 0 && (
				<MultiSelectFilter
					label={t("manufacturer")}
					options={manufacturers.map((m) => ({ value: m._id, label: m.name }))}
					selected={filters.manufacturerIds}
					onToggle={(id) => toggleMulti("manufacturerIds", id)}
					searchable={manufacturers.length > 8}
				/>
			)}

			<DiscModelFilter
				label={t("discModel")}
				selected={filters.discModelIds}
				onToggle={(id) => toggleMulti("discModelIds", id)}
				selectedManufacturerIds={filters.manufacturerIds}
			/>

			{visiblePlastics.length > 0 && (
				<MultiSelectFilter
					label={t("plastic")}
					options={visiblePlastics.map((p) => ({
						value: p.name,
						label: p.name,
					}))}
					selected={filters.plasticType}
					onToggle={(name) => toggleMulti("plasticType", name)}
					searchable={visiblePlastics.length > 8}
				/>
			)}

			<MultiSelectFilter
				label={t("discClass")}
				options={DISC_CLASSES.map((c) => ({
					value: c.value,
					label: t(`discClasses.${c.value}`),
				}))}
				selected={filters.discClass}
				onToggle={(val) => toggleMulti("discClass", val)}
			/>

			<FilterButton
				label={t("condition")}
				activeCount={filters.condition.length > 0 ? 1 : 0}
			>
				<ConditionSlider
					condition={filters.condition}
					onChange={(condition) => onChange({ condition })}
				/>
			</FilterButton>

			<MultiSelectFilter
				label={t("color")}
				options={COLORS.map((color) => ({
					value: color.name,
					label: t(`colors.${color.name}`),
					icon: <ColorDot color={color} />,
				}))}
				selected={filters.color}
				onToggle={(name) => toggleMulti("color", name)}
			/>

			<FilterButton label={t("flight")} activeCount={flightActive}>
				<FlightFilter filters={filters} onChange={onChange} />
			</FilterButton>

			<FilterButton label={t("price")} activeCount={priceActive}>
				<RangeSlider
					min={0}
					max={300}
					step={5}
					filterMin={filters.priceMin}
					filterMax={filters.priceMax}
					onChange={(min, max) => onChange({ priceMin: min, priceMax: max })}
					unit="€"
				/>
			</FilterButton>

			<FilterButton label={t("weight")} activeCount={weightActive}>
				<RangeSlider
					min={130}
					max={200}
					step={1}
					filterMin={filters.weightMin}
					filterMax={filters.weightMax}
					onChange={(min, max) => onChange({ weightMin: min, weightMax: max })}
					unit="g"
				/>
			</FilterButton>

			<MultiSelectFilter
				label={t("shipsTo")}
				options={SHIPS_TO_OPTIONS.map((o) => ({
					value: o.value,
					label: t(`shipsToOptions.${o.value}`),
				}))}
				selected={filters.shipsTo}
				onToggle={(val) => toggleMulti("shipsTo", val)}
			/>

			{availableCountries.length > 0 && (
				<MultiSelectFilter
					label={t("country")}
					options={availableCountries.map((c) => ({ value: c, label: c }))}
					selected={filters.country}
					onToggle={(c) => toggleMulti("country", c)}
					searchable={availableCountries.length > 8}
				/>
			)}

			{userHasCoords ? (
				<FilterButton
					label={t("distance")}
					activeCount={filters.distanceKm !== "" ? 1 : 0}
				>
					<div className="flex flex-col gap-3 p-1 w-[220px]">
						{userCity && (
							<div className="flex items-center gap-1.5 text-[11px] text-muted-foreground px-1">
								<MapPin className="size-3 shrink-0" />
								<span className="truncate">{userCity}</span>
							</div>
						)}
						<DistanceSlider
							value={filters.distanceKm}
							onChange={(km) => onChange({ distanceKm: km })}
						/>
					</div>
				</FilterButton>
			) : (
				<button
					type="button"
					disabled
					title={t("distanceNoLocation")}
					className="h-8 shrink-0 inline-flex items-center gap-1.5 rounded-full border border-border px-3 text-xs font-medium text-muted-foreground/50 cursor-not-allowed"
				>
					<MapPin className="size-3" />
					{t("distance")}
				</button>
			)}

			{hasAny && (
				<Button
					variant="ghost"
					size="sm"
					className="h-8 gap-1.5 rounded-full px-3 text-xs text-muted-foreground hover:text-foreground"
					onClick={() => onChange(EMPTY_FILTERS)}
				>
					<X className="size-3" />
					{t("clearAll")}
				</Button>
			)}
		</>
	);
}

export function FilterBar({
	filters,
	availableCountries,
	availablePlasticNames,
	onChange,
	userCity,
	userHasCoords,
}: FilterBarProps) {
	const { t } = useTranslation("browse");
	const { data: plastics = [] } = useQuery(
		convexQuery(api.plastics.listAll, {}),
	);
	const { data: manufacturers = [] } = useQuery(
		convexQuery(api.manufacturers.list, {}),
	);

	const visiblePlastics =
		availablePlasticNames !== null
			? plastics.filter((p) => availablePlasticNames.includes(p.name))
			: plastics;

	const priceActive =
		filters.priceMin !== "" || filters.priceMax !== "" ? 1 : 0;
	const weightActive =
		filters.weightMin !== "" || filters.weightMax !== "" ? 1 : 0;
	const flightActive =
		filters.speedMin !== "" ||
		filters.speedMax !== "" ||
		filters.glideMin !== "" ||
		filters.glideMax !== "" ||
		filters.turnMin !== "" ||
		filters.turnMax !== "" ||
		filters.fadeMin !== "" ||
		filters.fadeMax !== ""
			? 1
			: 0;

	const hasAny =
		filters.discClass.length > 0 ||
		filters.condition.length > 0 ||
		filters.color.length > 0 ||
		filters.shipsTo.length > 0 ||
		filters.country.length > 0 ||
		filters.plasticType.length > 0 ||
		priceActive > 0 ||
		weightActive > 0 ||
		filters.discModelIds.length > 0 ||
		filters.manufacturerIds.length > 0 ||
		flightActive > 0 ||
		filters.distanceKm !== "";

	const activeCount = countActiveFilters(filters);

	const controlProps: FilterControlsProps = {
		filters,
		availableCountries,
		onChange,
		manufacturers,
		visiblePlastics,
		priceActive,
		weightActive,
		flightActive,
		hasAny,
		userCity,
		userHasCoords,
	};

	return (
		<>
			{/* Mobile: single "Filters" button opening a bottom Sheet (< md) */}
			<div className="flex items-center gap-2 md:hidden">
				<Sheet>
					<SheetTrigger asChild>
						<Button
							variant="outline"
							size="sm"
							className="relative h-9 gap-2 rounded-full px-4 text-sm font-medium"
						>
							<Filter className="size-3.5" />
							{t("filters")}
							{activeCount > 0 && (
								<Badge className="ml-0.5 h-5 min-w-5 rounded-full px-1.5 text-[10px] font-bold">
									{activeCount}
								</Badge>
							)}
						</Button>
					</SheetTrigger>
					<SheetContent side="bottom" className="max-h-[85svh] overflow-y-auto">
						<SheetHeader className="pb-2">
							<SheetTitle>{t("filters")}</SheetTitle>
						</SheetHeader>
						<div className="flex flex-wrap gap-2 px-4 pb-6">
							<FilterControls {...controlProps} />
						</div>
					</SheetContent>
				</Sheet>
			</div>

			{/* Desktop: inline chip row (>= md) */}
			<div className="hidden md:flex md:flex-wrap md:gap-2">
				<FilterControls {...controlProps} />
			</div>
		</>
	);
}
